import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import type {
  CollectionHierarchyQuery,
  CollectionNode,
} from "src/types/collection/collectionTypes";
import { useCollectionStore } from "src/store/collection/collectionStore";
import {
  buildVisibleRows,
  selectCollectionError,
  selectCollectionLoading,
  selectCollectionPageData,
  selectCollectionQuery,
  selectExpandedIds,
} from "src/store/collection/collectionSelectors";
import CollectionUpsertModal from "src/components/collection/CollectionUpsertModal";

export default function CollectionManagePage() {
  const query = useCollectionStore(selectCollectionQuery);
  const pageData = useCollectionStore(selectCollectionPageData);
  const loading = useCollectionStore(selectCollectionLoading);
  const error = useCollectionStore(selectCollectionError);
  const expandedIds = useCollectionStore(selectExpandedIds);

  const setQuery = useCollectionStore((s) => s.setQuery);
  const fetchHierarchy = useCollectionStore((s) => s.fetchHierarchy);
  const toggleExpand = useCollectionStore((s) => s.toggleExpand);
  const expandAllVisible = useCollectionStore((s) => s.expandAllVisible);
  const collapseAll = useCollectionStore((s) => s.collapseAll);
  const deleteCollection = useCollectionStore((s) => s.deleteCollection);

  /** ===== 모달 상태 ===== */
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [editing, setEditing] = useState<CollectionNode | null>(null);

  useEffect(() => {
    fetchHierarchy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.page, query.size, query.order]);

  const rows = useMemo(() => {
    const roots = pageData?.content || [];
    return buildVisibleRows(roots, expandedIds);
  }, [pageData, expandedIds]);

  const onChangeQuery = (patch: Partial<CollectionHierarchyQuery>) => {
    setQuery({ ...patch, page: 0 });
  };

  return (
    <Wrap>
      <Header>
        <Title>컬렉션 관리</Title>
        <Actions>
          <SmallBtn type="button" onClick={expandAllVisible}>
            전체 펼치기
          </SmallBtn>
          <SmallBtn type="button" onClick={collapseAll}>
            전체 접기
          </SmallBtn>
          <PrimaryBtn
            type="button"
            onClick={() => {
              setModalMode("create");
              setEditing(null);
              setModalOpen(true);
            }}
          >
            컬렉션 생성
          </PrimaryBtn>
        </Actions>
      </Header>

      {/* ===== 검색 필터 ===== */}
      <FilterBar>
        <Field>
          <Label>컬렉션명</Label>
          <Input
            value={query.collectionName || ""}
            onChange={(e) => onChangeQuery({ collectionName: e.target.value })}
            placeholder="예: 봄 신상품"
          />
        </Field>

        {/* <Field>
          <Label>정렬</Label>
          <Select
            value={query.order || "displayOrder,asc"}
            onChange={(e) => setQuery({ order: e.target.value })}
          >
            <option value="displayOrder,asc">진열순(오름차순)</option>
            <option value="displayOrder,desc">진열순(내림차순)</option>
            <option value="createdAt,desc">생성일(최신)</option>
          </Select>
        </Field> */}

        <SearchBtn type="button" onClick={fetchHierarchy} disabled={loading}>
          검색
        </SearchBtn>
      </FilterBar>

      {error && <ErrorBox>{error}</ErrorBox>}

      {/* ===== 테이블 ===== */}
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th style={{ width: 200 }}>컬렉션명</th>
              <th style={{ width: 90 }}>컬렉션코드</th>
              <th style={{ width: 90 }}>컬렉션ID</th>
              <th style={{ width: 110 }}>활성</th>
              <th style={{ width: 220 }}>노출기간</th>
              <th style={{ width: 180 }}>관리</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8}>
                  <LoadingBox>불러오는 중...</LoadingBox>
                </td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8}>
                  <EmptyBox>데이터가 없습니다.</EmptyBox>
                </td>
              </tr>
            )}

            {!loading &&
              rows.map((r) => (
                <tr key={r.node.collectionId}>
                  <td>
                    <NameCell>
                      <Indent style={{ width: r.level * 18 }} />
                      {r.hasChildren ? (
                        <ExpandBtn type="button" onClick={() => toggleExpand(r.node.collectionId)}>
                          {r.isExpanded ? "▾" : "▸"}
                        </ExpandBtn>
                      ) : (
                        <ExpandPlaceholder />
                      )}
                      <NameText title={r.node.collectionName}>{r.node.collectionName}</NameText>
                      <ChildCount>{r.hasChildren ? `(${r.childrenCount})` : ""}</ChildCount>
                    </NameCell>
                  </td>
                  <td>{r.node.collectionCode}</td>
                  <td>{r.node.collectionId}</td>
                  <td>{r.node.isFeatured ? "Y" : "N"}</td>
                  <td>{r.node.isActive ? "Y" : "N"}</td>
                  <td>
                    {fmtDate(r.node.displayStartDate ?? "")} ~{" "}
                    {fmtDate(r.node.displayEndDate ?? "")}
                  </td>
                  <td>
                    <ManageBtns>
                      <SmallBtn
                        type="button"
                        onClick={() => {
                          setModalMode("edit");
                          setEditing(r.node);
                          setModalOpen(true);
                        }}
                      >
                        수정
                      </SmallBtn>
                      <DangerBtn
                        type="button"
                        onClick={() => {
                          if (window.confirm("삭제할까요?")) {
                            deleteCollection(r.node.collectionId);
                          }
                        }}
                      >
                        삭제
                      </DangerBtn>
                    </ManageBtns>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </TableWrap>

      {/* ===== 페이지네이션 ===== */}
      <Pagination>
        <SmallBtn
          type="button"
          disabled={!pageData || pageData.page <= 0 || loading}
          onClick={() => {
            if (!pageData) return;
            setQuery({ page: Math.max(0, pageData.page - 1) });
          }}
        >
          이전
        </SmallBtn>

        <PageInfo>
          {pageData
            ? `${pageData.page + 1} / ${pageData.totalPages} (총 ${pageData.totalElements})`
            : "-"}
        </PageInfo>

        <SmallBtn
          type="button"
          disabled={!pageData || pageData.page >= pageData.totalPages - 1 || loading}
          onClick={() => {
            if (!pageData) return;
            setQuery({
              page: Math.min(pageData.totalPages - 1, pageData.page + 1),
            });
          }}
        >
          다음
        </SmallBtn>
      </Pagination>

      {/* ===== 생성/수정 모달 ===== */}
      <CollectionUpsertModal
        open={modalOpen}
        mode={modalMode}
        initial={editing}
        onClose={() => setModalOpen(false)}
      />
    </Wrap>
  );
}

/* ===== util ===== */
function fmtDate(iso?: string) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function pad2(n: number) {
  return n < 10 ? `0${n}` : `${n}`;
}

/* ===== styles ===== */
/* (네가 올린 스타일 그대로라 생략 안 하고 유지) */

const Wrap = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const FilterBar = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 180px 180px 220px 120px;
  gap: 10px;
  align-items: end;
  padding: 12px;
  border: 1px solid #eee;
  border-radius: 10px;
  margin-bottom: 12px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.div`
  font-size: 12px;
  color: #666;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  width: 250px;
`;

const Select = styled.select`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
`;

const SearchBtn = styled.button`
  height: 36px;
  border-radius: 8px;
  border: 0;
  background: #111;
  color: #fff;
  cursor: pointer;
  width: 150px;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const TableWrap = styled.div`
  overflow: auto;
  border: 1px solid #eee;
  border-radius: 10px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 1200px;

  th,
  td {
    border-bottom: 1px solid #f1f1f1;
    padding: 10px 12px;
    font-size: 13px;
    text-align: left;
    white-space: nowrap;
  }

  th {
    position: sticky;
    top: 0;
    background: #fafafa;
    z-index: 1;
    font-weight: 700;
  }
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Indent = styled.div`
  flex: 0 0 auto;
`;

const ExpandBtn = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid #ddd;
  background: #fff;
  cursor: pointer;
`;

const ExpandPlaceholder = styled.div`
  width: 26px;
  height: 26px;
`;

const NameText = styled.div`
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChildCount = styled.div`
  font-size: 12px;
  color: #888;
`;

const Period = styled.div`
  color: #444;
`;

const ManageBtns = styled.div`
  display: flex;
  gap: 8px;
`;

const SmallBtn = styled.button`
  height: 32px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: #fff;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const PrimaryBtn = styled(SmallBtn)`
  background: #111;
  color: #fff;
  border: 0;
`;

const DangerBtn = styled(SmallBtn)`
  border-color: #ffdddd;
  color: #c40000;
`;

const LoadingBox = styled.div`
  padding: 16px;
  color: #666;
`;

const EmptyBox = styled.div`
  padding: 22px;
  color: #777;
`;

const ErrorBox = styled.div`
  padding: 10px 12px;
  border: 1px solid #ffd7d7;
  background: #fff3f3;
  color: #b00000;
  border-radius: 10px;
  margin-bottom: 12px;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
`;

const PageInfo = styled.div`
  font-size: 13px;
  color: #444;
`;
